import { supabase } from '../lib/supabase';

export const APP_STORE_URL = 'https://apps.apple.com/app/ridepulse/id6753354214';

// Android package id matches the live Google Play listing. Used for public
// download badges only — never for the Internal Testing opt-in link, which is
// not exposed on the public website.
export const GOOGLE_PLAY_URL =
  'https://play.google.com/store/apps/details?id=app.ridepulse.android';

// Local fallback — used until the DB value loads. Each platform has its own
// flag so Apple and Android can be toggled independently from the admin console.
let _isAppPublished = false;
let _isAndroidPublished = false;
let _fetched = false;
let _inFlight: Promise<boolean> | null = null;

export function getIsAppPublished(): boolean {
  return _isAppPublished;
}

export function getIsAndroidPublished(): boolean {
  return _isAndroidPublished;
}

// Fetches both platform flags in one pass. The app_config RPC is a generic
// key-value lookup, so a second key (`is_android_published`) needs no backend
// change — it is just another row, defaulting to null/false (Coming Soon) until
// the admin toggles it live.
export async function fetchAppPublishedStatus(): Promise<boolean> {
  if (_fetched) return _isAppPublished;
  if (_inFlight) return _inFlight;
  _inFlight = doFetchAppPublishedStatus();
  try {
    return await _inFlight;
  } finally {
    _inFlight = null;
  }
}

async function doFetchAppPublishedStatus(): Promise<boolean> {
  try {
    const [{ data: appleData, error: appleError }, { data: androidData, error: androidError }] =
      await Promise.all([
        supabase.rpc('get_app_config', { config_key: 'is_app_published' }),
        supabase.rpc('get_app_config', { config_key: 'is_android_published' }),
      ]);
    if (!appleError && appleData !== null) {
      _isAppPublished = appleData === true;
    }
    if (!androidError && androidData !== null) {
      _isAndroidPublished = androidData === true;
    }
    _fetched = true;
  } catch {
    // keep fallbacks
  }
  return _isAppPublished;
}

export async function setAppPublishedStatus(published: boolean): Promise<void> {
  const { error } = await supabase.rpc('admin_set_app_config', {
    config_key: 'is_app_published',
    config_value: published,
  });
  if (error) throw error;
  _isAppPublished = published;
}

export async function setAndroidPublishedStatus(published: boolean): Promise<void> {
  const { error } = await supabase.rpc('admin_set_app_config', {
    config_key: 'is_android_published',
    config_value: published,
  });
  if (error) throw error;
  _isAndroidPublished = published;
}
